import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import { FichaContenedor, FilaFicha } from "../../solicitudCheque/components/FichaSolicitud";
import { mensajeError, previsualizarEquipos, registrarEquipos } from "../services/ordenCompraService";

const hoy = () => new Date().toISOString().slice(0, 10);
const ESTADOS_ITEM = {
  nuevo: { texto: "Se registrará", color: "success" },
  existe: { texto: "Ya existe", color: "error" },
  repetido: { texto: "Repetido", color: "warning" },
  invalido: { texto: "Falta un dato", color: "error" },
};

// Registrar los equipos que llegaron con una línea de compra: se elige el archivo Excel (o se pega la lista), se verifica y se confirma.
export default function RegistrarEquiposDialog({ open, ordenId, formato, onClose, onRegistrados }) {
  const [fuente, setFuente] = useState(0); // 0 Excel, 1 pegar
  const [archivo, setArchivo] = useState(null);
  const [texto, setTexto] = useState("");
  const [datos, setDatos] = useState({ estado: "Almacen", ciudad: "", fecha: hoy(), observacion: "" });
  const [pre, setPre] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const inputArchivo = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFuente(0); setArchivo(null); setTexto(""); setPre(null); setError("");
    setDatos({ estado: "Almacen", ciudad: formato?.ciudad_fija || "", fecha: hoy(), observacion: "" });
  }, [open, formato]);

  const set = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value }));
  const linea = formato?.linea;

  const verificar = async () => {
    setError("");
    if (fuente === 0 && !archivo) return setError("Elige el archivo Excel (.xlsx).");
    if (fuente === 1 && !texto.trim()) return setError("Pega la lista de equipos, uno por línea.");
    setCargando(true);
    try {
      const fd = new FormData();
      if (fuente === 0) fd.append("excel", archivo); else fd.append("texto", texto);
      const { data } = await previsualizarEquipos(ordenId, linea.id, fd);
      setPre(data);
    } catch (e) {
      setPre(null);
      setError(mensajeError(e, "No se pudo verificar la lista."));
    } finally {
      setCargando(false);
    }
  };

  const registrar = async () => {
    if (!datos.ciudad) return setError("Elige la sucursal.");
    setGuardando(true);
    setError("");
    try {
      const { data } = await registrarEquipos(ordenId, linea.id, { ...datos, items: pre.items });
      onRegistrados(data);
    } catch (e) {
      setError(mensajeError(e, "No se pudieron registrar los equipos."));
    } finally {
      setGuardando(false);
    }
  };

  const esModem = formato?.familia === "modem";
  const esVarios = formato?.familia === "varios";
  const nuevos = pre?.resumen?.nuevos || 0;

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 800 }}>Registrar equipos recibidos</Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 400 }}>
          {linea?.detalle}{linea?.marca ? ` · ${linea.marca}` : ""} — faltan {formato?.disponibles} de {linea?.cantidad}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 1, bgcolor: "background.default", p: 1.5 }}>
        <FichaContenedor>
          <FilaFicha campos={[
            { etiqueta: "Entran como", control: (
              <ToggleButtonGroup exclusive size="small" color="primary" fullWidth value={datos.estado} onChange={(_, v) => v && setDatos((d) => ({ ...d, estado: v }))} sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 0.5, gap: 0.5 }, "&& .Mui-selected, && .Mui-selected:hover": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 800 } }}>
                {(formato?.estados || ["Almacen", "Inactivo"]).map((e) => <ToggleButton key={e} value={e}>{datos.estado === e && <CheckIcon sx={{ fontSize: 16 }} />}{e}</ToggleButton>)}
              </ToggleButtonGroup>
            ) },
            { etiqueta: "Sucursal *", control: (
              <TextField fullWidth size="small" select hiddenLabel value={datos.ciudad} onChange={set("ciudad")} disabled={!!formato?.ciudad_fija} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Sucursal" } }}>
                <MenuItem value="" disabled>Elegir…</MenuItem>
                {(formato?.ciudades || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            ) },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Fecha de compra", control: <TextField fullWidth size="small" type="date" hiddenLabel value={datos.fecha} onChange={set("fecha")} slotProps={{ htmlInput: { "aria-label": "Fecha de compra" } }} /> },
            { etiqueta: "Observación", control: <TextField fullWidth size="small" hiddenLabel value={datos.observacion} onChange={set("observacion")} slotProps={{ htmlInput: { maxLength: 150, "aria-label": "Observación" } }} /> },
          ]}
          />
        </FichaContenedor>

        <FichaContenedor>
          <Box sx={{ p: 1, display: "grid", gap: 1 }}>
            <Alert severity="info" sx={{ py: 0 }}>{formato?.columnas}. Sin encabezado; si lo trae, se omite.</Alert>
            <Tabs value={fuente} onChange={(_, v) => { setFuente(v); setPre(null); }} sx={{ minHeight: 32, "& .MuiTab-root": { minHeight: 32, py: 0, textTransform: "none", fontWeight: 700, fontSize: 13 } }}>
              <Tab label="Archivo Excel" />
              <Tab label="Pegar lista" />
            </Tabs>
            {fuente === 0 ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <input ref={inputArchivo} type="file" accept=".xlsx" style={{ display: "none" }} onChange={(e) => { setArchivo(e.target.files[0] || null); setPre(null); e.target.value = ""; }} />
                <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} sx={{ textTransform: "none" }} onClick={() => inputArchivo.current?.click()}>Elegir archivo .xlsx</Button>
                {archivo && <Chip size="small" label={archivo.name} onDelete={() => { setArchivo(null); setPre(null); }} />}
              </Box>
            ) : (
              <TextField
                fullWidth size="small" hiddenLabel multiline minRows={3} maxRows={8} value={texto} onChange={(e) => { setTexto(e.target.value); setPre(null); }}
                placeholder={esModem ? "ID, número de serie e IMEI separados por tabulación (se pega desde Excel), uno por línea" : esVarios ? "Serie y, opcional, IMEI o código; uno por línea" : "Una serie por línea"}
                slotProps={{ htmlInput: { "aria-label": "Lista de equipos" } }}
              />
            )}
            <Box><Button variant="contained" size="small" onClick={verificar} disabled={cargando} sx={{ textTransform: "none", fontWeight: 700 }}>{cargando ? <CircularProgress size={18} color="inherit" /> : "Verificar lista"}</Button></Box>
          </Box>
        </FichaContenedor>

        {pre && (
          <FichaContenedor>
          <Box sx={{ p: 1, display: "grid", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Chip size="small" color="success" label={`${pre.resumen.nuevos} se registrarán`} sx={{ fontWeight: 700 }} />
              {pre.resumen.existentes > 0 && <Chip size="small" color="error" variant="outlined" label={`${pre.resumen.existentes} ya existen`} />}
              {pre.resumen.repetidos > 0 && <Chip size="small" color="warning" variant="outlined" label={`${pre.resumen.repetidos} repetidos`} />}
              {pre.resumen.invalidos > 0 && <Chip size="small" color="error" variant="outlined" label={`${pre.resumen.invalidos} con datos faltantes`} />}
            </Box>
            {pre.excede && <Alert severity="error" sx={{ py: 0 }}>Esta línea es de {pre.cantidad} equipo(s) y ya tiene {pre.registrados}: solo caben {pre.disponibles} más y hay {pre.resumen.nuevos} para registrar. Quita filas del archivo.</Alert>}
            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Fila</TableCell>
                    {esModem && <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>ID</TableCell>}
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>{esModem ? "N.º de serie" : "Serie"}</TableCell>
                    {(esModem || esVarios) && <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>IMEI / código</TableCell>}
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Resultado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pre.items.map((i, k) => (
                    <TableRow key={k} sx={i.estado !== "nuevo" ? { bgcolor: "action.hover" } : undefined}>
                      <TableCell sx={{ fontSize: 12.5 }}>{i.fila}</TableCell>
                      {esModem && <TableCell sx={{ fontSize: 12.5 }}>{i.id}</TableCell>}
                      <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{i.serie || "—"}</TableCell>
                      {(esModem || esVarios) && <TableCell sx={{ fontSize: 12.5 }}>{i.emai}</TableCell>}
                      <TableCell><Chip size="small" variant="outlined" color={ESTADOS_ITEM[i.estado].color} label={i.detalle || ESTADOS_ITEM[i.estado].texto} sx={{ fontSize: 11.5 }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          </FichaContenedor>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button variant="contained" onClick={registrar} disabled={guardando || !pre || nuevos === 0 || pre.excede} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : `Registrar ${nuevos || ""} equipo${nuevos === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={7000} onClose={(_, m) => m !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
